import { useCallback, useEffect, useRef, useState } from 'react';

interface Params {
  readonly canStart: boolean;
  readonly perform: () => Promise<void>;
}

interface Result {
  readonly attempt: number;
  readonly retry: () => void;
}

export const useRetryableCreation = ({
  canStart,
  perform,
}: Readonly<Params>): Result => {
  const [attempt, setAttempt] = useState(0);

  const performRef = useRef<() => Promise<void>>(async () => {
    // Placeholder, replaced by the effect below before it can ever run.
  });

  useEffect(() => {
    performRef.current = perform;
  });

  const startedAttempt = useRef<number | null>(null);

  useEffect(() => {
    if (!canStart || startedAttempt.current === attempt) {
      return;
    }

    startedAttempt.current = attempt;
    void performRef.current();
  }, [attempt, canStart]);

  const retry = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  return { attempt, retry };
};
